import { Plus } from '@phosphor-icons/react';
import {
  Button,
  Checkbox,
  cn,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  SearchField,
  Tag,
  toast,
} from '@reelplicate/ui';
import { useId, useMemo, useState } from 'react';

import { useMedia } from './media-context';
import { MAX_TAG_LENGTH, type TagCount, tagCounts } from './tags';

type Change = 'add' | 'remove';
/** Keyed by the tag ignoring case. */
type Draft = Map<string, { tag: string; change: Change }>;

/**
 * Figma "Popover · Tag" (408:3392): adds and removes tags on the selection.
 * Each tag's checkbox shows whether all, some or none of the selection has
 * it; nothing is saved until Apply.
 */
export function BulkTag({
  selectedIds,
  triggerClassName,
}: {
  selectedIds: string[];
  triggerClassName: string;
}) {
  const { assets, tagAssets } = useMedia();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<Draft>(new Map());

  const count = selectedIds.length;
  const items = `${count} ${count === 1 ? 'item' : 'items'}`;
  const counts = useMemo(() => tagCounts(assets, selectedIds), [assets, selectedIds]);

  // New tags sit at the top of the list while they're in the draft.
  const known = new Set(counts.map((c) => c.tag.toLowerCase()));
  const created: TagCount[] = [...draft.values()]
    .filter(({ tag }) => !known.has(tag.toLowerCase()))
    .map(({ tag }) => ({ tag, onSelected: 0, total: 0 }));
  const all = [...created, ...counts];

  const name = query.trim().replace(/\s+/g, ' ');
  const folded = name.toLowerCase();
  // Matches first; the rest stay listed.
  const matches = (c: TagCount) => c.tag.toLowerCase().includes(folded);
  const shown = [...all.filter(matches), ...all.filter((c) => !matches(c))];
  const existing = all.find((c) => c.tag.toLowerCase() === folded);
  const canCreate = name.length > 0 && name.length <= MAX_TAG_LENGTH && !existing;

  const setChange = (tag: string, change: Change | undefined) =>
    setDraft((prev) => {
      const next = new Map(prev);
      if (change) next.set(tag.toLowerCase(), { tag, change });
      else next.delete(tag.toLowerCase());
      return next;
    });

  /** Add a typed tag to the selection (or tick an existing one). */
  const addTyped = () => {
    if (!canCreate && !existing) return;
    const target = existing ?? { tag: name, onSelected: 0, total: 0 };
    if (target.onSelected < count) setChange(target.tag, 'add');
    else setChange(target.tag, undefined);
    setQuery('');
  };

  const reset = () => {
    setQuery('');
    setDraft(new Map());
  };

  const apply = () => {
    const changes = [...draft.values()];
    const add = changes.filter((c) => c.change === 'add').map((c) => c.tag);
    const remove = changes.filter((c) => c.change === 'remove').map((c) => c.tag);
    tagAssets(selectedIds, { add, remove });
    toast.success(remove.length === 0 ? `Tagged ${items}` : `Updated tags on ${items}`);
    setOpen(false);
    reset();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            triggerClassName,
            'data-[state=open]:bg-[rgba(248,249,239,0.12)]',
          )}
        >
          <Plus aria-hidden className="size-4" />
          Tag
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        aria-label="Tag items"
        className="rounded-[14px] [&_[data-slot=popover-body]]:p-0"
        footer={
          <>
            <PopoverClose asChild>
              <Button variant="ghost" size="sm">
                Cancel
              </Button>
            </PopoverClose>
            <Button size="sm" disabled={draft.size === 0} onClick={apply}>
              Apply
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 px-3.5 pt-3.5 pb-2.5">
          <p className="text-label-md text-text-primary">Tag {items}</p>
          <SearchField
            aria-label="Find or add a tag"
            placeholder="Find or add a tag"
            shortcut={false}
            maxLength={MAX_TAG_LENGTH}
            value={query}
            onValueChange={setQuery}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name) {
                e.preventDefault();
                addTyped();
              }
            }}
          />
        </div>
        <ul
          aria-label="Tags"
          className="flex max-h-64 flex-col overflow-y-auto px-1.5 pb-2"
        >
          {shown.length === 0 && !canCreate && (
            <li className="text-body-sm text-text-secondary px-2 py-2.5">
              No tags yet. Type one to add it.
            </li>
          )}
          {shown.map((c) => (
            <TagOption
              key={c.tag.toLowerCase()}
              count={c}
              selected={count}
              change={draft.get(c.tag.toLowerCase())?.change}
              onChange={(change) => setChange(c.tag, change)}
            />
          ))}
          {canCreate && (
            <li>
              <button
                type="button"
                onClick={addTyped}
                className="bg-brand-subtle text-label-sm text-text-brand focus-visible:shadow-focus-ring flex w-full items-center gap-2 rounded-md p-2.5 text-left outline-none"
              >
                <Plus aria-hidden className="size-4" />
                <span className="flex-1">Add tag “{name}”</span>
                <span aria-hidden className="text-caption text-text-tertiary">
                  ↵
                </span>
              </button>
            </li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function TagOption({
  count: { tag, onSelected },
  selected,
  change,
  onChange,
}: {
  count: TagCount;
  /** How many items are selected. */
  selected: number;
  change: Change | undefined;
  onChange: (change: Change | undefined) => void;
}) {
  const id = useId();
  const original =
    onSelected === selected ? true : onSelected > 0 ? 'indeterminate' : false;
  const checked = change === 'add' ? true : change === 'remove' ? false : original;
  const on = change === 'add' ? selected : change === 'remove' ? 0 : onSelected;
  return (
    <li className="hover:bg-bg-subtle flex items-center gap-2 rounded-md px-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(next) => {
          if (next === true) onChange(original === true ? undefined : 'add');
          else onChange(original === false ? undefined : 'remove');
        }}
      />
      <label
        htmlFor={id}
        className="flex min-w-0 flex-1 items-center justify-between gap-2 px-2.5 py-2"
      >
        <Tag>{tag}</Tag>{' '}
        {on > 0 && (
          <span className="text-caption text-text-tertiary shrink-0">
            {on === selected ? `on all ${selected}` : `on ${on} of ${selected}`}
          </span>
        )}
      </label>
    </li>
  );
}
